import axios from 'axios';

const API_URL = process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com';

const INFLUENCER_TOKEN_KEY = 'influencer_token';
const INFLUENCER_DATA_KEY = 'influencer_data';

// Auth helpers
export const getInfluencerToken = (): string | null => localStorage.getItem(INFLUENCER_TOKEN_KEY);
export const getInfluencerData = () => {
  const data = localStorage.getItem(INFLUENCER_DATA_KEY);
  return data ? JSON.parse(data) : null;
};
export const isInfluencerLoggedIn = (): boolean => !!getInfluencerToken();
export const logoutInfluencer = () => {
  localStorage.removeItem(INFLUENCER_TOKEN_KEY);
  localStorage.removeItem(INFLUENCER_DATA_KEY);
};

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${getInfluencerToken()}` },
});

// Set password (first time)
export const influencerSetPassword = async (email: string, code: string, password: string) => {
  const response = await axios.post(`${API_URL}/api/influencer/set-password`, { email, code, password });
  return response.data;
};

// Forgot password — sends an email if account exists. Backend always returns success.
export const influencerForgotPassword = async (email: string) => {
  const response = await axios.post(`${API_URL}/api/influencer/forgot-password`, { email });
  return response.data;
};

// Reset password using the emailed token.
export const influencerResetPassword = async (token: string, password: string) => {
  const response = await axios.post(`${API_URL}/api/influencer/reset-password`, { token, password });
  return response.data;
};

// Login
export const influencerLogin = async (email: string, password: string) => {
  const response = await axios.post(`${API_URL}/api/influencer/login`, { email, password });
  const { token, influencer } = response.data;
  localStorage.setItem(INFLUENCER_TOKEN_KEY, token);
  localStorage.setItem(INFLUENCER_DATA_KEY, JSON.stringify(influencer));
  return { token, influencer };
};

// Dashboard
export interface InfluencerDashboard {
  influencer: {
    name: string;
    code: string;
    email: string;
    platform: string;
    profile_image: string | null;
    social_url: string | null;
    apple_campaign_link: string | null;
    google_campaign_link: string | null;
  };
  stats: {
    total_signups: number;
    total_subscriptions: number;
    paid_conversions: number;
    trials: number;
    conversion_rate: number;
    devices: { ios: number; android: number; unknown: number };
    deltas?: { signups: number; trials: number; paid: number };
  };
  commission: {
    rate: number;
    currency: string;
    earned: number;
    paid: number;
    pending: number;
  };
  monthly: Record<string, { signups: number; subscriptions: number; trials: number }>;
  recent_events: Array<{
    event_type: string;
    device_type: string;
    date: string;
    user: { username: string } | null;
    is_trial: boolean;
    is_first_paid: boolean;
    amount: number;
    currency: string;
    commission_amount: number;
    provider_store: string | null;
  }>;
}

export const fetchInfluencerDashboard = async (): Promise<InfluencerDashboard> => {
  const response = await axios.get(`${API_URL}/api/influencer/dashboard`, authHeaders());
  return response.data;
};

// Promo materials
export interface PromoMaterials {
  code: string;
  apple_link: string;
  google_link: string;
  sample_captions: Array<{
    platform: string;
    text: string;
  }>;
}

export const fetchPromoMaterials = async (): Promise<PromoMaterials> => {
  const response = await axios.get(`${API_URL}/api/influencer/promo-materials`, authHeaders());
  return response.data;
};

// Payment profile
export interface PaymentProfile {
  name: string;
  email: string;
  country: string;
  payment_method: string;
  payment_email: string;
  bank_name: string;
  bank_account_holder: string;
  bank_account_number: string;
  bank_routing_swift: string;
  tax_id: string;
  commission_currency: string;
}

export const fetchInfluencerProfile = async (): Promise<PaymentProfile> => {
  const response = await axios.get(`${API_URL}/api/influencer/profile`, authHeaders());
  return response.data;
};

export const updateInfluencerProfile = async (data: Partial<PaymentProfile>) => {
  const response = await axios.put(`${API_URL}/api/influencer/profile`, data, authHeaders());
  return response.data;
};
